
export const GA_TRACKING_ID = process.env.NEXT_PUBLIC_GA_TRACKING_ID || 'G-FLTSJST4D1';

// Google Analytics 4 Configuration
declare global {
  interface Window {
    gtag: (
      command: 'config' | 'event' | 'js' | 'set',
      targetId: string,
      config?: Record<string, any>
    ) => void;
    dataLayer: any[];
  }
}

// Test GA_TRACKING_ID format
const isValidGATrackingId = (id: string) => {
  return id.startsWith('G-') && id.length === 12;
};

// Initialize Google Analytics
export const initGA = () => {
  if (!isValidGATrackingId(GA_TRACKING_ID)) {
    return;
  }
  
  if (typeof window !== 'undefined') {
    // Check if gtag is already loaded
    if (typeof window.gtag === 'function') {
      return;
    }

    // Check if script already exists
    const existingScript = document.querySelector(`script[src*="googletagmanager.com"]`);
    if (existingScript) {
      return;
    }

    // Initialize dataLayer first
    window.dataLayer = window.dataLayer || [];
    
    // Define gtag function before loading script
    window.gtag = function () {
      window.dataLayer.push(arguments);
    };

    // Load gtag script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`;
    
    script.onload = () => {
      // Initialize gtag after script loads
      window.gtag('js', new Date().toISOString());
      window.gtag('config', GA_TRACKING_ID, {
        page_path: window.location.pathname,
        debug_mode: process.env.NODE_ENV === 'development',
      });
    };
    
    document.head.appendChild(script);
  }
};

// Track pageview
export const pageview = (url: string) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('config', GA_TRACKING_ID, {
      page_path: url,
    });
  }
};

// Track custom events
type GTagEvent = {
  action: string;
  category: string;
  label: string;
  value?: number;
};

export const event = ({ action, category, label, value }: GTagEvent) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value,
    });
  }
};

// Track specific events
export const trackPageView = (url: string) => {
  pageview(url);
};

export const trackButtonClick = (buttonName: string, page: string) => {
  event({
    action: 'click',
    category: 'button',
    label: `${buttonName} - ${page}`,
  });
};

export const trackSearch = (searchTerm: string) => {
  event({
    action: 'search',
    category: 'engagement',
    label: searchTerm,
  });
};

export const trackListingView = (listingId: string, make: string, model: string) => {
  event({
    action: 'view_item',
    category: 'listing',
    label: `${make} ${model}`,
  });
};

export const trackContactForm = (formType: string) => {
  event({
    action: 'submit_form',
    category: 'contact',
    label: formType,
  });
};
